import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  acknowledgeReceipt,
  deleteReceipt as apiDeleteReceipt,
  getInbox,
  getNextUnacknowledged,
  getUnreadCount,
  markReceiptRead,
} from "../services/AdminMessageService";
import { useAuth } from "./AuthContext";
import type { AdminMessageReceiptDto } from "../api/generated";

const POLL_MS = 60000;

export interface AdminMessageContextValue {
  unreadCount: number;
  nextUnacknowledged: AdminMessageReceiptDto | null;
  inbox: AdminMessageReceiptDto[];
  inboxLoading: boolean;
  refreshBadge: () => Promise<void>;
  refreshInbox: () => Promise<void>;
  markRead: (receiptId: number) => Promise<void>;
  acknowledge: (receiptId: number) => Promise<void>;
  deleteReceipt: (receiptId: number) => Promise<void>;
}

const AdminMessageContext = createContext<AdminMessageContextValue | null>(null);

export function useAdminMessages(): AdminMessageContextValue | null {
  return useContext(AdminMessageContext);
}

export function AdminMessageProvider({ children }: { children: ReactNode }) {
  const { currentUser, isCompanyGroupAdmin } = useAuth();
  // A COMPANY_GROUP_ADMIN has no inbox and can never have one: broadcast receipts are created per
  // company for users holding ADMIN or TRAINER in that company (AdminBroadcastService.send), and a
  // group admin is company-less with neither authority. It is also not on the /admin/** floor
  // (SecurityConfig: SYSTEM_ADMIN|ADMIN|TRAINER), so every inbox call is a guaranteed rejection —
  // which is exactly what it was: two failed requests on mount and two more every POLL_MS.
  //
  // Read from the context (not the localStorage-backed isCompanyGroupAdminAuthority) so this
  // re-evaluates when the identity flips on impersonate / stop-impersonation.
  const hasInbox = !isCompanyGroupAdmin?.();
  const [unreadCount, setUnreadCount] = useState(0);
  const [nextUnacknowledged, setNextUnacknowledged] = useState<AdminMessageReceiptDto | null>(null);
  const [inbox, setInbox] = useState<AdminMessageReceiptDto[]>([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshBadge = useCallback(async () => {
    if (!currentUser || !hasInbox) return;
    try {
      const [countRes, nextRes] = await Promise.all([
        getUnreadCount(),
        getNextUnacknowledged(),
      ]);
      setUnreadCount(countRes?.data?.count ?? 0);
      // 204 No Content → axios returns empty data
      setNextUnacknowledged(nextRes?.data && nextRes.data.id ? nextRes.data : null);
    } catch (err) {
      // silent — bell just stays at last known state
    }
  }, [currentUser, hasInbox]);

  const refreshInbox = useCallback(async () => {
    if (!currentUser || !hasInbox) return;
    setInboxLoading(true);
    try {
      const res = await getInbox();
      setInbox(res?.data ?? []);
    } finally {
      setInboxLoading(false);
    }
  }, [currentUser, hasInbox]);

  const markRead = useCallback(async (receiptId: number) => {
    await markReceiptRead(receiptId);
    setInbox(prev => prev.map(r => r.id === receiptId && !r.readAt
      ? { ...r, readAt: new Date().toISOString() }
      : r));
    refreshBadge();
  }, [refreshBadge]);

  const acknowledge = useCallback(async (receiptId: number) => {
    await acknowledgeReceipt(receiptId);
    setNextUnacknowledged(null);
    refreshBadge();
  }, [refreshBadge]);

  const deleteReceipt = useCallback(async (receiptId: number) => {
    await apiDeleteReceipt(receiptId);
    setInbox(prev => prev.filter(r => r.id !== receiptId));
    refreshBadge();
  }, [refreshBadge]);

  useEffect(() => {
    // No inbox (logged out, or a group admin) → reset to empty and never start the poll. Without the
    // hasInbox arm a group admin kept an interval alive that fired two rejected requests a minute.
    if (!currentUser || !hasInbox) {
      setUnreadCount(0);
      setNextUnacknowledged(null);
      setInbox([]);
      return undefined;
    }
    refreshBadge();
    pollRef.current = setInterval(refreshBadge, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [currentUser, hasInbox, refreshBadge]);

  const value: AdminMessageContextValue = {
    unreadCount,
    nextUnacknowledged,
    inbox,
    inboxLoading,
    refreshBadge,
    refreshInbox,
    markRead,
    acknowledge,
    deleteReceipt,
  };

  return (
    <AdminMessageContext.Provider value={value}>
      {children}
    </AdminMessageContext.Provider>
  );
}
